import { useMemo, useState } from "react";
import {
	Box,
	Button,
	Divider,
	IconButton,
	InputAdornment,
	Modal,
	Paper,
	TextField,
	Typography,
} from "@mui/material";
import { Clear, CloseOutlined, Search } from "@mui/icons-material";
import WorkspacesOutlinedIcon from "@mui/icons-material/WorkspacesOutlined";
import { useTheme } from "@mui/material/styles";
import { LoadingButton } from "@mui/lab";

import api from "../../../services/api";
import DatagridComponent from "../../../components/datagridComponent";
import useStyles from "./styles";

export default function ModalCriarGrupo({
	openModal,
	setOpenModal,
	clientes = [],
	setSnackbar,
	onCreated,
}) {
	const theme = useTheme();
	const styles = useStyles(theme);

	const [nome, setNome] = useState("");
	const [searchTerm, setSearchTerm] = useState("");
	const [selectedClientes, setSelectedClientes] = useState([]);
	const [loading, setLoading] = useState(false);
	const [nullCheck, setNullCheck] = useState(false);

	// Elegíveis: exclui outros grupos e clientes que já pertencem a um grupo
	const clientesElegiveis = useMemo(
		() =>
			clientes?.filter(
				(cliente) => !cliente?.isGrupoEconomico && !cliente?.grupoEconomicoId,
			) || [],
		[clientes],
	);

	const totalJaAgrupados = useMemo(
		() =>
			clientes?.filter(
				(cliente) => !cliente?.isGrupoEconomico && cliente?.grupoEconomicoId,
			)?.length || 0,
		[clientes],
	);

	const rowsMap = useMemo(
		() => new Map(clientesElegiveis.map((obj) => [obj.id, obj])),
		[clientesElegiveis],
	);

	const filteredClientes = clientesElegiveis.filter((cliente) => {
		const search = searchTerm.toLowerCase();
		return (
			cliente?.razaoSocial?.toLowerCase().includes(search) ||
			cliente?.nomeFantasia?.toLowerCase().includes(search) ||
			cliente?.cnpj?.includes(search) ||
			cliente?.clienteErpId?.toLowerCase().includes(search)
		);
	});

	const columns = [
		{
			field: "razaoSocial",
			headerName: "Cliente",
			flex: 1,
			renderCell: (params) => {
				return `${params.row.clienteErpId} - ${
					params.row.nomeFantasia || params.row.razaoSocial || "Sem nome"
				}`;
			},
		},
	];

	const handleCancelar = () => {
		setOpenModal(false);
		setNome("");
		setSearchTerm("");
		setSelectedClientes([]);
		setNullCheck(false);
	};

	const handleCriarGrupo = async () => {
		setNullCheck(true);
		if (!nome?.trim()) {
			setSnackbar({
				children: "Informe o nome do grupo",
				severity: "error",
			});
			return;
		}
		if (!selectedClientes?.length) {
			setSnackbar({
				children: "Selecione ao menos um cliente para o grupo",
				severity: "error",
			});
			return;
		}

		try {
			setLoading(true);
			const response = await api.post("/interno/create/grupoEconomico", {
				nome: nome.trim(),
				membrosIds: selectedClientes.map((cliente) => cliente.id),
			});
			setSnackbar({
				children: "Grupo econômico criado com sucesso",
				severity: "success",
			});
			onCreated?.(response?.data);
			handleCancelar();
		} catch (error) {
			console.error(error);
			setSnackbar({
				children:
					error?.response?.data?.message ||
					error?.response?.data?.error ||
					"Erro ao criar o grupo econômico",
				severity: "error",
			});
		} finally {
			setLoading(false);
		}
	};

	return (
		<Modal
			sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
			open={openModal}
			onClose={handleCancelar}
		>
			<Paper sx={styles.container}>
				<Box sx={styles.containerTitulo}>
					<Typography variant="h6" fontWeight={600}>
						Criar Grupo Econômico
					</Typography>
					<IconButton onClick={handleCancelar}>
						<CloseOutlined />
					</IconButton>
				</Box>
				<Divider />
				<Box sx={styles.containerForm}>
					<TextField
						label="Nome do grupo"
						id="nomeGrupo"
						variant="filled"
						color="primary"
						size="small"
						fullWidth
						autoComplete="off"
						value={nome}
						onChange={(e) => setNome(e?.target?.value)}
						helperText={
							!nome?.trim() && nullCheck
								? "Necessário informar o nome do grupo"
								: ""
						}
						error={!nome?.trim() && nullCheck}
					/>

					<Box sx={styles.boxClientes}>
						<Box sx={styles.containerClientes}>
							<Box>
								<Box sx={styles.titleClientes}>
									<WorkspacesOutlinedIcon sx={{ fontSize: "22px" }} />
									<Typography sx={{ fontWeight: "bold" }}>
										Clientes do grupo
									</Typography>
								</Box>
								<Typography
									sx={{
										fontWeight: "bold",
										fontSize: "12px",
										opacity: 0.5,
									}}
								>
									{`Selecione os clientes que farão parte do grupo econômico.${
										totalJaAgrupados > 0
											? ` ${totalJaAgrupados} cliente(s) já pertencem a outro grupo e não são exibidos.`
											: ""
									}`}
								</Typography>
							</Box>

							{/* Campo de Busca */}
							<TextField
								size="small"
								variant="outlined"
								placeholder="Buscar cliente."
								value={searchTerm}
								onChange={(e) => setSearchTerm(e.target.value)}
								sx={{ minWidth: "300px" }}
								InputProps={{
									startAdornment: (
										<InputAdornment position="start">
											<Search fontSize="small" />
										</InputAdornment>
									),
									endAdornment: (
										<InputAdornment position="end">
											{searchTerm && (
												<IconButton
													size="small"
													onClick={() => setSearchTerm("")}
												>
													<Clear fontSize="small" />
												</IconButton>
											)}
										</InputAdornment>
									),
								}}
							/>
						</Box>

						<Paper variant="outlined" sx={styles.paperDatagrid}>
							<DatagridComponent
								options={{
									checkboxSelection: true,
									onRowSelectionModelChange: (currentSelectionIds) => {
										const previouslySelected = selectedClientes || [];

										// 1. IDs da lista visível no momento (com busca aplicada)
										const visibleIds = filteredClientes.map((c) => c.id);

										// 2. Mantém quem já estava selecionado mas não aparece na busca atual
										const itemsNotVisible = previouslySelected.filter(
											(item) => !visibleIds.includes(item.id),
										);

										// 3. Resolve os IDs selecionados visíveis para os objetos
										const itemsNewlySelected = currentSelectionIds
											?.filter((id) => visibleIds.includes(id))
											?.map((id) => rowsMap.get(id))
											?.filter(Boolean);

										// 4. Une os dois grupos
										setSelectedClientes([
											...itemsNotVisible,
											...itemsNewlySelected,
										]);
									},
									rowSelectionModel: selectedClientes?.map((c) => c?.id),
									disableRowSelectionOnClick: true,
								}}
								rows={filteredClientes || []}
								columns={columns}
							/>
						</Paper>
						<Typography sx={{ fontSize: "12px", opacity: 0.7 }}>
							{`${selectedClientes?.length || 0} cliente(s) selecionado(s)`}
						</Typography>
					</Box>
				</Box>
				<Divider />
				<Box sx={styles.containerButtons}>
					<Button variant="outlined" onClick={handleCancelar}>
						Cancelar
					</Button>
					<LoadingButton
						variant="contained"
						loading={loading}
						onClick={handleCriarGrupo}
					>
						Criar Grupo
					</LoadingButton>
				</Box>
			</Paper>
		</Modal>
	);
}
